import {v4 as uuid} from "uuid";
import {notifyError} from "@/api/ErrorNotification.ts";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {useEncounterLights} from "@/encounter/lights/EncounterLightHooks.ts";
import {movedDungeonLightOverride} from "@/encounter/lights/EncounterLights.ts";
import {LIGHT_PRESETS} from "@/dungeons/lighting/LightPresets.ts";
import type {Dungeon} from "@/dungeons/model/Dungeon.ts";
import type {WorldPoint} from "@/dungeons/model/DungeonGeometry.ts";

type DungeonLevel = Dungeon["levels"][number];

export default function useEncounterLightActions(activeLevel: DungeonLevel | undefined,
    onCreated: (levelId: string, lightId: string) => void) {
    const {combatants} = useCombatants();
    const {state: lightState, saveLight, saveOverride} = useEncounterLights();

    const placeLight = async (position: WorldPoint, combatantId?: string) => {
        const levelId = combatants.find(item => item.id === combatantId)?.levelId ?? activeLevel?.id;
        if (!levelId) return;
        const id = uuid();
        try {
            await saveLight({id, levelId, position, combatantId, preset: "TORCH",
                ...LIGHT_PRESETS.TORCH, enabled: true});
            onCreated(levelId, id);
        } catch (error) {notifyError(error, "Das Licht konnte nicht angelegt werden.");}
    };
    const addAttachedLight = (combatantId: string) => {
        const combatant = combatants.find(item => item.id === combatantId);
        if (combatant) void placeLight(combatant.position, combatantId);
    };
    const moveLight = async (id: string, position: WorldPoint) => {
        const encounterLight = lightState.lights.find(item => item.id === id);
        const dungeonLight = activeLevel?.geometry.lights.find(item => item.id === id);
        try {
            if (encounterLight) await saveLight({...encounterLight, position});
            else if (dungeonLight) {
                const override = lightState.overrides.find(item => item.lightId === id);
                await saveOverride(movedDungeonLightOverride(dungeonLight, override, position));
            }
        } catch (error) {notifyError(error, "Das Licht konnte nicht verschoben werden.");}
    };

    return {placeLight, addAttachedLight, moveLight};
}
